import { set } from 'lodash-es';

function forEachEntity(entity, callback) {
  const dfs = (obj) => {
    if (!obj || typeof obj !== 'object') {
      return;
    }
    for (const [k, v] of Object.entries(obj)) {
      if (Array.isArray(v)) {
        v.forEach(dfs);
      } else if (v && typeof v === 'object') {
        dfs(v);
      } else if (typeof v === 'string') {
        callback(k, v, obj);
      }
    }
  };

  dfs(entity);
}

export function changeI18nKeyInEntity(formEntity, oldValue, newValue, ns) {
  const newFormEntity = JSON.parse(JSON.stringify(formEntity));

  forEachEntity(newFormEntity, (k, v, obj) => {
    if (k.endsWith('_i18n_key') && v === oldValue) {
      const nsKey = k.replace('_i18n_key', '_i18n_ns');
      if ((obj[nsKey] || 'default') === ns) {
        obj[k] = newValue;
      }
    }
  });

  return newFormEntity;
}

export function changeI18nNsInEntity(formEntity, oldValue, newValue, key) {
  const newFormEntity = JSON.parse(JSON.stringify(formEntity));

  forEachEntity(newFormEntity, (k, v, obj) => {
    if (k.endsWith('_i18n_key') && v === key) {
      const nsKey = k.replace('_i18n_key', '_i18n_ns');
      if ((obj[nsKey] || 'default') === oldValue) {
        obj[nsKey] = newValue;
      }
    }
  });

  return newFormEntity;
}

export function compressToTranslate(data, allowedLanguages, forceReplace) {
  const toTranslate = {};
  let allValues = data.map((item) => {
    if (!forceReplace && allowedLanguages.every((lng) => item[lng])) {
      return undefined;
    }
    const usedTranslatedKey = item.defaultValue
      ? 'defaultValue'
      : allowedLanguages.find((x) => item[x]); // 如果没有 defaultValue，取第一个有值的译文来作为翻译依据
    return item[usedTranslatedKey];
  });

  // 去重能减少大量的 i18n 资源
  allValues = Array.from(new Set(allValues)).filter((v) => v !== null && v !== undefined);

  // 最后去掉 i18n 资源的长字符串 key，用 index 做 key 能进一步较很大的数据量，
  // 同时 toTranslate 是对象格式而不是数组是为了保证翻译后的资源能和原始的一一对应，防止 ai 翻译过后数组长度不匹配
  allValues.forEach((value, index) => {
    toTranslate[index] = value;
  });
  return toTranslate;
}

export function deCompressToTranslate({
  toTranslate,
  resByLng,
  originalData,
  lngs,
  allowedLanguages,
  forceReplace,
}) {
  const mapByLng = {};

  lngs.forEach((lng) => {
    const response = resByLng[lng];
    const map = {};
    for (const [k, v] of Object.entries(toTranslate)) {
      map[v] = response[k];
    }
    mapByLng[lng] = map;
  });

  // 这里必须先遍历 data 再遍历 lngs，因为会直接修改数据对象，先遍历了 lngs 可能会导致 usedTranslatedKey 是 zh-CN
  // 但是 ai 返回的 zh-CN 的翻译变了，导致后续 en-US 和 ja-JP 的时候找到错误的 usedDefaultValue 而导致
  // 下面的 map[usedDefaultValue] 找不到翻译过后的译文
  return originalData.map((item) => {
    const usedTranslatedKey = item.defaultValue
      ? 'defaultValue'
      : allowedLanguages.find((x) => item[x]);
    const usedDefaultValue = item[usedTranslatedKey];

    lngs.forEach((lng) => {
      if (!forceReplace && item[lng]) {
        // return item;
        return;
      }

      const map = mapByLng[lng];
      if (item[lng] !== map[usedDefaultValue]) {
        item[lng] = map[usedDefaultValue];
      }
    });
    return item;
  });
}

export function getAutoTranslateChange({
  toTranslate,
  resByLng,
  originalData,
  lngs,
  allowedLanguages,
  forceReplace,
}) {
  const mapByLng = {};

  lngs.forEach((lng) => {
    const response = resByLng[lng];
    const map = {};
    for (const [k, v] of Object.entries(toTranslate)) {
      map[v] = response[k];
    }
    mapByLng[lng] = map;
  });

  const change = {};

  originalData.forEach((item) => {
    const usedTranslatedKey = item.defaultValue
      ? 'defaultValue'
      : allowedLanguages.find((x) => item[x]);
    const usedDefaultValue = item[usedTranslatedKey];

    lngs.forEach((lng) => {
      if (!forceReplace && item[lng]) {
        return;
      }

      const map = mapByLng[lng];
      if (item[lng] !== map[usedDefaultValue]) {
        set(change, [lng, item.namespace, item.key], map[usedDefaultValue]);
      }
    });
  });

  return change;
}

export function getAllNeedTranslateRowData(gridRef) {
  const rowData = [];
  gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
    if (!node.data.key?.startsWith('standard|>')) {
      rowData.push(node.data);
    }
  });
  return rowData;
}

export function getToTranslateRanges(params, allowedLanguages) {
  const ranges = params.api.getCellRanges();
  return ranges
    .filter((range) => {
      return range.columns.find((column) => allowedLanguages.includes(column.colId));
    })
    .map((range) => {
      return {
        ...range,
        columns: range.columns.filter((column) => allowedLanguages.includes(column.colId)),
      };
    })
    .map((range) => {
      const { startRow, endRow, columns } = range;

      const originalData = [];
      params.api.forEachNodeAfterFilterAndSort((node) => {
        if (
          node.rowIndex >= startRow.rowIndex &&
          node.rowIndex <= endRow.rowIndex &&
          !node.data.key?.startsWith('standard|>')
        ) {
          originalData.push(node.data);
        }
      });
      const lngs = columns.map((column) => column.colId);
      return { lngs, originalData };
    })
    .filter((item) => {
      return Object.keys(item.originalData).length > 0;
    });
}
